import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  defaultIfEmpty,
  filter,
  interval,
  map,
  Observable,
  of,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import {
  AdminOnlinePaymentDTO,
  ChargesType,
  TrainingInfo,
} from '@rewaa-team/types';
import { Subscription } from 'chargebee-typescript/lib/resources';
import {
  AccountDetailsDTO,
  AccountExistsResponse,
  AttachedItemDTO,
  CheckoutEstimateReqDTO,
  EstimateV2Response,
  PaginatedAccountListDTO,
  CursorAccountListDTO,
  PlanAttachmentsResponse,
  RegisterNewMerchantDTO,
  UpdateMerchantResponseDTO,
  EstimateResponseLicenseKey,
  ShippingAddress,
  CheckoutRenewEstimateReqDTO,
  PaymentProofDTO,
  PartialPaymentDTO,
  CreditNoteRequest,
  BillingHistoryResponseDTO,
  RetryAndResubmissionPayload,
  CreateCreditNoteResponseDTO,
  CreditNoteRequestForPartiallyPaidInvoice,
  Country,
  ResetPasswordCode,
  FreeDeviceProductsResponse,
  CashbackEntry,
  BulkCashbackRequest,
  BulkCashbackResponse,
} from './types';

const API_URL = `/api/nucleus`;
const INVENTORY_API_URL = `/api/inventory-service`;
const MARKETING_API_URL = `/api/marketing`;

@Injectable()
export class AccountService {
  constructor(private readonly http: HttpClient) {}

  getAccounts(
    page: number,
    limit: number,
    searchParam?: string,
  ): Observable<PaginatedAccountListDTO> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (searchParam) {
      params = params.set('searchParam', searchParam);
    }

    return this.http.get<PaginatedAccountListDTO>(`${API_URL}/admin/accounts`, {
      params,
    });
  }

  getAccountsCursor(
    limit: number,
    afterId?: number,
    searchParam?: string,
    searchField?: string,
  ): Observable<CursorAccountListDTO> {
    let params = new HttpParams()
      .set('pagination', 'cursor')
      .set('limit', limit.toString());
    if (afterId !== undefined)
      params = params.set('afterId', afterId.toString());
    if (searchParam) params = params.set('searchParam', searchParam);
    if (searchField) params = params.set('searchField', searchField);
    return this.http.get<CursorAccountListDTO>(`${API_URL}/admin/accounts`, {
      params,
    });
  }

  getAccount(email: string): Observable<AccountDetailsDTO> {
    return this.http.get<AccountDetailsDTO>(
      `${API_URL}/admin/account-details/${email}`,
    );
  }

  getActiveSkuCount(tenantId: number): Observable<number> {
    return this.http.get<number>(
      `${INVENTORY_API_URL}/admin/variants/active-sku-count`,
      {
        params: { tenantId: tenantId.toString() },
      },
    );
  }

  getFreeDeviceProducts(
    accountId: string,
  ): Observable<FreeDeviceProductsResponse> {
    return this.http.get<FreeDeviceProductsResponse>(
      `${API_URL}/admin/free-device-products/${accountId}`,
    );
  }

  getSubscriptionWithScheduledChange(
    subscriptionId: string,
  ): Observable<Subscription | false> {
    return this.http.get<Subscription | false>(
      `${API_URL}/admin/scheduled-subscription/${subscriptionId}`,
    );
  }

  issueCreditNote(
    payload: CreditNoteRequest,
  ): Observable<CreateCreditNoteResponseDTO> {
    return this.http.post<CreateCreditNoteResponseDTO>(
      `${API_URL}/admin/credit-note`,
      payload,
    );
  }

  getInvoiceAvailableQuantities(
    invoiceId: string,
  ): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(
      `${API_URL}/admin/invoice/${invoiceId}/available-quantities`,
    );
  }

  issueCreditNoteForPartiallyPaidInvoice(
    payload: CreditNoteRequestForPartiallyPaidInvoice,
  ): Observable<CreateCreditNoteResponseDTO> {
    return this.http.post<CreateCreditNoteResponseDTO>(
      `${API_URL}/admin/credit-note/partially-paid-invoice`,
      payload,
    );
  }

  resetAccount(
    email: string,
    payload: {
      recordId: string;
      url: string;
      titleMeta: string;
    },
  ): Observable<void> {
    return this.http.post<void>(`${API_URL}/reset-merchant/admin/initiate`, {
      email,
      zohoDetails: payload,
    });
  }

  getBillingHistoryBySubscriptionId(
    subscriptionId: string,
  ): Observable<BillingHistoryResponseDTO> {
    return this.http.get<BillingHistoryResponseDTO>(
      `${API_URL}/admin/invoices/billing-history/${subscriptionId}`,
    );
  }

  getInvoicesByOwnerEmail(
    ownerEmail: string,
  ): Observable<BillingHistoryResponseDTO> {
    return this.http.get<BillingHistoryResponseDTO>(
      `${API_URL}/admin/invoices/by-owner/${ownerEmail}`,
    );
  }

  getSubscriptionStatusBySubscriptionId(
    subscriptionId: string,
  ): Observable<boolean> {
    const params = new HttpParams().set('subscriptionId', subscriptionId);
    return this.http.get<boolean>(`${API_URL}/subscription/status`, { params });
  }

  getAllActivePlans(): Observable<AttachedItemDTO[]> {
    return this.http.get<AttachedItemDTO[]>(`${API_URL}/admin/plans`);
  }

  getPlanAttachmentsByPlanId(
    planItemId: string,
    itemPriceId: string,
    chargesType: ChargesType = ChargesType.Hardware,
  ): Observable<PlanAttachmentsResponse> {
    const params = new HttpParams().set('chargesType', chargesType);
    if (itemPriceId && itemPriceId !== '') {
      return this.http.get<PlanAttachmentsResponse>(
        `${API_URL}/admin/plans/${planItemId}/${itemPriceId}/attached-items`,
        {
          params,
        },
      );
    }

    return this.http.get<PlanAttachmentsResponse>(
      `${API_URL}/admin/plans/${planItemId}/attached-items`,
      {
        params,
      },
    );
  }

  getSubscriptionStatus(
    subscriptionId: string,
  ): Observable<{ subscriptionStatus: string | undefined }> {
    return this.http.get<{ subscriptionStatus: string | undefined }>(
      `${API_URL}/admin/subscription-status/${subscriptionId}`,
    );
  }

  /**
   * Polls the subscription status endpoint until the subscription is ready.
   * Returns true if subscription becomes ready, false if timeout.
   */
  waitForSubscriptionReady(
    subscriptionId: string,
    maxAttempts = 60,
    intervalMs = 2000,
  ): Observable<boolean> {
    return interval(intervalMs).pipe(
      take(maxAttempts),
      switchMap(() => this.getSubscriptionStatus(subscriptionId)),
      filter(
        (status) =>
          !!status.subscriptionStatus &&
          status.subscriptionStatus !== 'no_subscription',
      ),
      take(1),
      map(() => true),
      timeout(maxAttempts * intervalMs + 1000),
      catchError(() => of(false)),
      defaultIfEmpty(false),
    );
  }

  getEstimate(
    estimateReq: CheckoutEstimateReqDTO,
  ): Observable<EstimateV2Response> {
    return this.http.post<EstimateV2Response>(
      `${API_URL}/admin/checkout/estimate`,
      estimateReq,
    );
  }

  getChargesEstimate(
    estimateReq: CheckoutEstimateReqDTO,
  ): Observable<EstimateV2Response> {
    return this.http.post<EstimateV2Response>(
      `${API_URL}/admin/checkout/charges/estimate`,
      estimateReq,
    );
  }

  getRenewEstimate(
    estimateReq: CheckoutRenewEstimateReqDTO,
  ): Observable<EstimateV2Response> {
    return this.http.post<EstimateV2Response>(
      `${API_URL}/admin/checkout/renew/estimate`,
      estimateReq,
    );
  }

  updateAdminSubscription(payload: AdminOnlinePaymentDTO): Observable<void> {
    const isPartial = payload.isPartial ? '?ispartial=true' : '';
    return this.http.put<void>(
      `${API_URL}/admin/subscription${isPartial}`,
      payload,
    );
  }

  getAccountInfo(email: string): Observable<AccountExistsResponse> {
    return this.http.get<AccountExistsResponse>(
      `${API_URL}/admin/merchant-info/${email}`,
    );
  }

  isMonshaatEligible(email: string): Observable<boolean> {
    if (!email) return of(false);

    return this.getAccountInfo(email).pipe(
      map((response: AccountExistsResponse) => {
        const crmAccount = response?.crmAccount;
        const monshaatOrderNumber =
          crmAccount?.crm?.monshaatOrderNumber?.trim();
        const monshaatCompanyName =
          crmAccount?.crm?.monshaatCompanyName?.trim();
        return Boolean(
          crmAccount?.foundCRMAccount &&
            monshaatOrderNumber &&
            monshaatCompanyName,
        );
      }),
      catchError(() => of(false)),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMerchantAndCRMInfo(accountId: string): Observable<any> {
    return this.http.get<any>(
      `${API_URL}/admin/merchant-crm-info/${accountId}`,
    );
  }

  registerNewMerchant(payload: RegisterNewMerchantDTO): Observable<{
    success: boolean;
    hasCRMAccount?: boolean;
    subscriptionID?: string;
  }> {
    return this.http.post<{ success: boolean }>(
      `${API_URL}/admin/merchant-info`,
      payload,
    );
  }

  updateMerchantInfo(
    email: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any,
  ): Observable<UpdateMerchantResponseDTO> {
    return this.http.put<UpdateMerchantResponseDTO>(
      `${API_URL}/admin/merchant-info/${email}`,
      payload,
    );
  }

  getLicenseKeyEstimateItems(
    subscriptionId: string,
    licenseKey: string,
  ): Observable<EstimateResponseLicenseKey> {
    return this.http.get<EstimateResponseLicenseKey>(
      `${API_URL}/admin/license-key/${licenseKey}/estimate/${subscriptionId}`,
    );
  }

  updateLicenseKeySubscription(
    subscriptionId: string,
    licenseKey: string,
    shippingDetails: ShippingAddress | undefined,
    crmAccountDetails: TrainingInfo,
  ): Observable<{ id: string; quantity: number }[] | undefined> {
    return this.http.put<{ id: string; quantity: number }[] | undefined>(
      `${API_URL}/admin/license-key/activate`,
      {
        id: subscriptionId,
        licenseKey,
        shippingAddress: shippingDetails,
        crmAccountDetails,
      },
    );
  }

  uploadPaymentReceipt(
    base64: string,
    email: string,
  ): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${API_URL}/admin/payment-receipt`, {
      base64,
      email,
    });
  }

  getPaymentProofs(
    chargebeeInvoiceId: string,
    subscriptionId: string,
  ): Observable<PaymentProofDTO> {
    return this.http.get<PaymentProofDTO>(
      `${API_URL}/admin/invoice/payment-proofs?chargebeeInvoiceId=${chargebeeInvoiceId}&subscriptionId=${subscriptionId}`,
    );
  }

  updateCRMEmailInfo(
    accountID: string,
    body: { email: string },
  ): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${API_URL}/admin/crm-info/${accountID}`,
      body,
    );
  }

  postPartialPayment(
    body: PartialPaymentDTO,
    invoiceId: string,
  ): Observable<void> {
    return this.http.post<void>(
      `${API_URL}/admin/partial-payment/${invoiceId}`,
      body,
    );
  }

  // checkCRMAccountAvaliabilty
  checkCRMAccountAvaliabilty(email: string): Observable<void> {
    return this.http.get<void>(`${API_URL}/admin/check-crm-account/${email}`);
  }

  updateInvoiceBilling(data: RetryAndResubmissionPayload): Observable<void> {
    return this.http.post<void>(
      `${API_URL}/admin/invoice/billing/${data.id}`,
      data,
    );
  }

  getPaymentMethod(): Observable<string> {
    return this.http.get<string>(`${API_URL}/admin/payment-types`);
  }

  retryInvoicesOrCreditNotes(
    data: RetryAndResubmissionPayload,
  ): Observable<void> {
    const { id, type } = data;

    if (type === 'invoice') {
      return this.http.post<void>(
        `${API_URL}/zatca-invoicing/invoice/${id}`,
        data,
      );
    }

    return this.http.post<void>(
      `${API_URL}/zatca-invoicing/credit-note/${id}`,
      data,
    );
  }

  getCountries(): Observable<Country[]> {
    return this.http
      .get<Country[]>(`/api/nucleus/country`)
      .pipe(map((response) => response.map((country) => new Country(country))));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getProratedCreditNoteEstimate(data: {
    subscriptionId: string;
    invoiceId: string;
  }): Observable<any[]> {
    return this.http.post<any[]>(
      `${API_URL}/admin/credit-note/prorated-estimate`,
      data,
    );
  }

  generatePartialProratedCreditNote(
    data: CreditNoteRequest,
  ): Observable<CreateCreditNoteResponseDTO> {
    return this.http.post<CreateCreditNoteResponseDTO>(
      `${API_URL}/admin/prorated-credit-note/partially-paid-invoice`,
      data,
    );
  }

  getResetPasswordCode(email: string): Observable<ResetPasswordCode> {
    return this.http.get<ResetPasswordCode>(
      `${API_URL}/admin/reset-password-code/${email}`,
    );
  }

  getBackdoorAccessLink(merchantEmail: string): Observable<{ link: string }> {
    return this.http.get<{ link: string }>(
      `${API_URL}/admin/backlink/${merchantEmail}`,
    );
  }

  getBackdoorAccessConfig(merchantEmail: string): Observable<{
    isAccessible: boolean;
    accessExpiryDate: string;
    message?: string;
  }> {
    return this.http.get<{
      isAccessible: boolean;
      accessExpiryDate: string;
      message?: string;
    }>(`${API_URL}/admin/backlink/${merchantEmail}/config`);
  }

  extendBackdoorAccess(
    merchantEmail: string,
    extensionDays: number,
  ): Observable<{
    isAccessible: boolean;
    accessExpiryDate: string;
  }> {
    return this.http.post<{
      isAccessible: boolean;
      accessExpiryDate: string;
    }>(`${API_URL}/admin/backlink/${merchantEmail}/extend`, { extensionDays });
  }

  importCBInvoices(invoiceIds: string[]): Observable<{
    processedInvoiceIds: string[];
    failedInvoiceIds: string[];
    skippedInvoiceIds: string[];
  }> {
    return this.http.post<{
      processedInvoiceIds: string[];
      failedInvoiceIds: string[];
      skippedInvoiceIds: string[];
    }>(`${API_URL}/admin/import-cb-invoices`, { invoiceIds });
  }

  getHardwareReplacementItems(accountId: string): Observable<
    {
      productCode: string;
      isActive: boolean;
      availableQty: number;
      alternatives: { productCode: string; isActive: boolean }[];
      replacementHistory: { productCode: string; qty: number }[];
    }[]
  > {
    return this.http.get<
      {
        productCode: string;
        isActive: boolean;
        availableQty: number;
        alternatives: { productCode: string; isActive: boolean }[];
        replacementHistory: { productCode: string; qty: number }[];
      }[]
    >(`${API_URL}/admin/hardware-replacement/${accountId}`);
  }

  addAccountWallet(payload: CashbackEntry): Observable<void> {
    return this.http.post<void>(
      `${MARKETING_API_URL}/admin/wallet/cashback`,
      payload,
    );
  }

  isMonshaaatOrderBlocked(orderId: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${API_URL}/admin/blocked-monshaat-orders/${orderId}/check`,
    );
  }

  getAllBlockedMonshaatOrders(): Observable<
    { orderId: string; createdAt: number }[]
  > {
    return this.http.get<{ orderId: string; createdAt: number }[]>(
      `${API_URL}/admin/blocked-monshaat-orders/all`,
    );
  }

  bulkAddCashback(
    payload: BulkCashbackRequest,
  ): Observable<BulkCashbackResponse> {
    return this.http.post<BulkCashbackResponse>(
      `${MARKETING_API_URL}/admin/wallet/cashback/bulk`,
      payload,
    );
  }

  getInvoiceNotes(invoiceId: string): Observable<string> {
    return this.http.get<string>(
      `${API_URL}/admin/invoice-notes/${invoiceId}`,
      { responseType: 'text' as 'json' },
    );
  }

  saveInvoiceNotes(invoiceId: string, notes: string): Observable<void> {
    return this.http.post<void>(`${API_URL}/admin/invoice-notes/${invoiceId}`, {
      notes,
    });
  }
}
